const STORAGE_KEY = 'gods-armor-wishlist';

/**
 * @typedef {Object} WishlistItem
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string} image
 * @property {string} price
 * @property {string} [comparePrice]
 * @property {string} [variantId]
 */

class GaWishlist {
  /** @returns {WishlistItem[]} */
  static getItems() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @param {WishlistItem[]} items */
  static saveItems(items, { silent = false } = {}) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    if (!silent) {
      document.dispatchEvent(new CustomEvent('ga-wishlist:updated', { detail: { count: items.length } }));
    }
  }

  /** @param {string} id */
  static has(id) {
    return GaWishlist.getItems().some((item) => String(item.id) === String(id));
  }

  /** @param {WishlistItem} item */
  static add(item) {
    if (!item?.id) return false;
    const items = GaWishlist.getItems();
    if (items.some((entry) => String(entry.id) === String(item.id))) return false;
    items.unshift(item);
    GaWishlist.saveItems(items);
    return true;
  }

  /** @param {string} id */
  static remove(id) {
    const items = GaWishlist.getItems().filter((item) => String(item.id) !== String(id));
    GaWishlist.saveItems(items);
  }

  /** @param {WishlistItem} item */
  static toggle(item) {
    if (GaWishlist.has(item.id)) {
      GaWishlist.remove(item.id);
      return false;
    }
    GaWishlist.add(item);
    return true;
  }

  static getCount() {
    return GaWishlist.getItems().length;
  }

  /** @param {HTMLElement} button */
  static itemFromButton(button) {
    const dataset = button.dataset;
    if (!dataset.productId) return null;

    return {
      id: dataset.productId,
      handle: dataset.productHandle || '',
      title: dataset.productTitle || '',
      url: dataset.productUrl || '',
      image: dataset.productImage || '',
      price: dataset.productPrice || '',
      comparePrice: dataset.productComparePrice || '',
      variantId: dataset.productVariantId || '',
    };
  }

  /** @param {HTMLElement} button */
  static syncButton(button) {
    const item = GaWishlist.itemFromButton(button);
    if (!item) return;

    const active = GaWishlist.has(item.id);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      active ? `Remove ${item.title} from wishlist` : `Add ${item.title} to wishlist`
    );
  }

  static syncAllButtons() {
    document.querySelectorAll('[data-ga-wishlist-toggle]').forEach((button) => {
      if (button instanceof HTMLElement) GaWishlist.syncButton(button);
    });
  }

  static updateHeaderCount() {
    const count = GaWishlist.getCount();
    document.querySelectorAll('[data-ga-wishlist-count]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.textContent = String(count);
      node.classList.toggle('hidden', count === 0);
      node.closest('[data-ga-wishlist-bubble]')?.classList.toggle('visually-hidden', count === 0);
    });
  }

  /** @param {HTMLElement} button */
  static handleToggle(button) {
    const item = GaWishlist.itemFromButton(button);
    if (!item) return;

    GaWishlist.toggle(item);
    GaWishlist.syncButton(button);
    GaWishlist.updateHeaderCount();
  }

  static bindToggleButtons(root = document) {
    root.querySelectorAll('[data-ga-wishlist-toggle]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (button.dataset.gaWishlistBound === 'true') return;
      button.dataset.gaWishlistBound = 'true';
      GaWishlist.syncButton(button);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        GaWishlist.handleToggle(button);
      });
    });
  }

  static async refreshItem(item) {
    if (!item.handle) return item;
    try {
      const response = await fetch(`/products/${item.handle}.js`, { credentials: 'same-origin' });
      if (!response.ok) return item;
      const product = await response.json();
      const variant = product.variants?.find((entry) => entry.available) || product.variants?.[0];
      const image = product.featured_image || item.image;
      const price = variant ? GaWishlist.formatMoney(variant.price) : item.price;
      const comparePrice =
        variant && variant.compare_at_price > variant.price ? GaWishlist.formatMoney(variant.compare_at_price) : '';

      return {
        ...item,
        title: product.title || item.title,
        url: `/products/${product.handle}`,
        image,
        price,
        comparePrice,
        variantId: variant ? String(variant.id) : item.variantId,
      };
    } catch {
      return item;
    }
  }

  static formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  static renderWishlistPage() {
    const grid = document.querySelector('[data-ga-wishlist-page-grid]');
    const empty = document.querySelector('[data-ga-wishlist-page-empty]');
    if (!grid || !empty) return;

    const items = GaWishlist.getItems();
    if (!items.length) {
      grid.innerHTML = '';
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    grid.innerHTML = items
      .map(
        (item) => `
          <article class="ga-wishlist-card" data-ga-wishlist-card data-product-id="${item.id}">
            <div class="ga-wishlist-card__media">
              <a href="${item.url}" class="ga-wishlist-card__image-link" tabindex="-1" aria-hidden="true">
                ${
                  item.image
                    ? `<img class="ga-wishlist-card__image" src="${item.image}" alt="${GaWishlist.escapeHtml(item.title)}" loading="lazy" width="480" height="600">`
                    : '<div class="ga-wishlist-card__image ga-wishlist-card__image--placeholder"></div>'
                }
              </a>
              <button
                type="button"
                class="ga-wishlist-card__remove"
                data-ga-wishlist-remove="${item.id}"
                aria-label="Remove ${GaWishlist.escapeHtml(item.title)} from wishlist"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="ga-wishlist-card__body">
              <h2 class="ga-wishlist-card__title">
                <a href="${item.url}">${GaWishlist.escapeHtml(item.title)}</a>
              </h2>
              <p class="ga-wishlist-card__price">
                ${
                  item.comparePrice
                    ? `<span class="ga-wishlist-card__price-sale">${item.price}</span><s class="ga-wishlist-card__price-compare">${item.comparePrice}</s>`
                    : item.price
                }
              </p>
              <div class="ga-wishlist-card__actions">
                <a class="ga-wishlist-card__view" href="${item.url}">View product</a>
                ${
                  item.variantId
                    ? `<button type="button" class="ga-wishlist-card__atc" data-ga-wishlist-atc="${item.variantId}">Add to cart</button>`
                    : ''
                }
              </div>
            </div>
          </article>
        `
      )
      .join('');

    grid.querySelectorAll('[data-ga-wishlist-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.getAttribute('data-ga-wishlist-remove');
        if (!id) return;
        GaWishlist.remove(id);
        GaWishlist.syncAllButtons();
        GaWishlist.updateHeaderCount();
        GaWishlist.renderWishlistPage();
      });
    });

    grid.querySelectorAll('[data-ga-wishlist-atc]').forEach((button) => {
      button.addEventListener('click', async () => {
        const variantId = button.getAttribute('data-ga-wishlist-atc');
        if (!variantId) return;
        button.setAttribute('disabled', 'disabled');
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
          });
          button.textContent = 'Added';
          document.dispatchEvent(new CustomEvent('cart:update'));
        } catch {
          button.removeAttribute('disabled');
        }
      });
    });

    Promise.all(items.map((item) => GaWishlist.refreshItem(item))).then((freshItems) => {
      GaWishlist.saveItems(freshItems, { silent: true });
      GaWishlist.updateCardPrices(grid, freshItems);
    });
  }

  static updateCardPrices(grid, items) {
    items.forEach((item) => {
      const card = grid.querySelector(`[data-ga-wishlist-card][data-product-id="${item.id}"]`);
      if (!card) return;
      const price = card.querySelector('.ga-wishlist-card__price');
      if (!price) return;
      price.innerHTML = item.comparePrice
        ? `<span class="ga-wishlist-card__price-sale">${item.price}</span><s class="ga-wishlist-card__price-compare">${item.comparePrice}</s>`
        : item.price;
    });
  }

  static escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  static init() {
    GaWishlist.bindToggleButtons();
    GaWishlist.updateHeaderCount();
    GaWishlist.renderWishlistPage();

    document.addEventListener('ga-wishlist:updated', () => {
      GaWishlist.updateHeaderCount();
      GaWishlist.renderWishlistPage();
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof HTMLElement) {
        GaWishlist.bindToggleButtons(event.target);
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => GaWishlist.init());
} else {
  GaWishlist.init();
}

window.GaWishlist = GaWishlist;
